import {
  Box,
  Divider,
  Chip,
  Stack,
  Typography,
  alpha,
  styled,
  useTheme,
} from "@mui/material";
import { Campaign, Pause, VerifiedUser } from "@mui/icons-material";
import { SparkLineChart } from "@mui/x-charts";
import Image from "mui-image";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import {
  ApiAttributionModel,
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiExternalEntityStatus,
  ApiMetric,
  ApiOperator,
  ApiProject,
  ApiUTMEntity,
} from "@incendium/api";
import { IChart, TChartData } from "Interfaces";
import {
  AttributionMetric,
  metricByAttribution,
  useChartData,
  useFormatMetric,
} from "features/analytics";
import { useMemo } from "react";
import { useFromToContext } from "Providers/FromToProvider";
import { percentageChange } from "Helpers/percentage";
import { useAttributionContext } from "Providers/AttributionProvider";
import { useMetricName } from "features/analytics/hooks/useMetricName";

const CardRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.common.white,
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
}));

const ProductImageWrap = styled(Box)(({ theme }) => ({
  width: 72,
  height: 72,
  flexShrink: 0,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  backgroundColor: theme.palette.grey[50],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const SectionCard = styled(Box)<{ colour: string }>(({ theme, colour }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${alpha(colour, 0.18)}`,
  overflow: "hidden",
  backgroundColor: theme.palette.common.white,
}));

const SectionHeader = styled(Stack)<{ colour: string }>(
  ({ theme, colour }) => ({
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor: alpha(colour, 0.08),
  }),
);

const SectionBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const MetricsRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr 1fr auto",
  gap: theme.spacing(1),
  alignItems: "start",
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  lineHeight: 1.2,
}));

const MetricValue = styled(Typography)<{ colour?: string }>(
  ({ theme, colour }) => ({
    fontSize: 15,
    fontWeight: 700,
    color: colour || theme.palette.text.primary,
    lineHeight: 1.3,
  }),
);

const VarianceRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr 1fr auto",
  gap: theme.spacing(1),
  alignItems: "center",
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.common.white,
}));

const SparklineWrap = styled(Box)({
  height: 56,
  width: "100%",
});

interface IAdStatCardProps {
  project: ApiProject;
  ad: ApiUTMEntity;
  publisherName?: string;
  data: TChartData;
  filters: ApiChartAttribute[];
}

function formatVariance(value: number) {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

function varianceColour(value: number, error: string, success: string) {
  if (value < 0) return error;
  if (value > 0) return success;
  return undefined;
}

function AdStatMetric({
  label,
  value,
  valueColor,
  hidden,
}: {
  label: string;
  value?: string;
  valueColor?: string;
  hidden?: boolean;
}) {
  if (hidden) {
    return <Box />;
  }

  return (
    <Box>
      <MetricLabel>{label}</MetricLabel>
      <MetricValue colour={valueColor}>{value}</MetricValue>
    </Box>
  );
}

function AdStatSparkline({ data, colour }: { data: number[]; colour: string }) {
  if (data.length < 2) {
    return <SparklineWrap />;
  }

  return (
    <SparklineWrap>
      <SparkLineChart
        data={data}
        curve="natural"
        area
        height={56}
        colors={[colour]}
        margin={{ top: 4, bottom: 0, left: 0, right: 0 }}
        sx={{
          "& .MuiAreaElement-root": { opacity: 0.28 },
        }}
        yAxis={{
          min: Math.min(...data),
          max: Math.max(...data),
        }}
      />
    </SparklineWrap>
  );
}

function AdStatCard({
  project,
  ad,
  publisherName = "Meta",
  data,
  filters,
}: IAdStatCardProps) {
  const theme = useTheme();
  const trackedColour = theme.palette.charts[0];
  const publisherColour = theme.palette.warning.light;
  const errorColour = theme.palette.error.main;
  const successColour = theme.palette.success.main;
  const formatMetric = useFormatMetric();
  const { chartOutput } = useFromToContext();
  const { model } = useAttributionContext();
  const metricToName = useMetricName();
  const resolvedRoasMetric = metricByAttribution(
    model || ApiAttributionModel.EBA,
    AttributionMetric.ROAS,
  ) as ApiMetric;
  const resolvedSalesCountMetric = metricByAttribution(
    model || ApiAttributionModel.EBA,
    AttributionMetric.SALES,
  ) as ApiMetric;

  const trendChart: IChart = useMemo(
    () => ({
      name: `{ad.name} Publisher Trend`,
      dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
      template: ApiChartTemplate.TREND,
      type: ApiChartType.GRAPH,
      yAxisKeys: [
        {
          key: "l",
          fields: [
            ApiMetric.METRIC_PUBLISHER_CLICKS,
            ApiMetric.METRIC_SESSIONS_COUNT,
          ],
        },
      ],
      attributes: [
        ...filters,
        {
          key: ApiDimension.DIMENSION_EXTERNAL_AD_ID,
          operator: ApiOperator.EQUAL,
          value: String(ad.id),
        },
      ],
    }),
    [ad.id, filters],
  );

  const trendChartData = useChartData(project, trendChart, chartOutput, {
    ignoreModel: true,
  });

  const publisherTrendPoints = useMemo(() => {
    if (!trendChartData?.chartData) {
      return [];
    }
    return trendChartData.chartData.data.map((item) =>
      Number(item[ApiMetric.METRIC_PUBLISHER_CLICKS]),
    );
  }, [trendChartData]);

  const trackedTrendPoints = useMemo(() => {
    if (!trendChartData?.chartData) {
      return [];
    }
    return trendChartData.chartData.data.map((item) =>
      Number(item[ApiMetric.METRIC_SESSIONS_COUNT]),
    );
  }, [trendChartData]);

  const variance = useMemo(() => {
    return {
      clicks: percentageChange(
        Number(data[ApiMetric.METRIC_PUBLISHER_CLICKS] || 0),
        Number(data[ApiMetric.METRIC_SESSIONS_COUNT] || 0),
      ),
      sales: percentageChange(
        Number(data[ApiMetric.METRIC_PUBLISHER_SALES_COUNT] || 0),
        Number(data[resolvedSalesCountMetric] || 0),
      ),
      roas: percentageChange(
        Number(data[ApiMetric.METRIC_PUBLISHER_ROAS] || 0),
        Number(data[resolvedRoasMetric] || 0),
      ),
    };
  }, [data, resolvedRoasMetric, resolvedSalesCountMetric]);

  const entityStatusToLabel = (entityStatus: ApiExternalEntityStatus) => {
    switch (entityStatus) {
      case ApiExternalEntityStatus.EXTERNAL_ENTITY_STATUS_PAUSED:
        return "Paused";
      case ApiExternalEntityStatus.EXTERNAL_ENTITY_STATUS_DRAFT:
        return "Draft";
      default:
        return "Active";
    }
  };

  return (
    <CardRoot>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <ProductImageWrap>
          {ad.imageUrl ? (
            <Image
              src={ad.imageUrl}
              alt={ad.name}
              height={72}
              width={72}
              fit="contain"
              duration={0}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No image
            </Typography>
          )}
        </ProductImageWrap>

        <Box sx={{ height: 48 }}>
          <Divider orientation="vertical" />
        </Box>

        <Box minWidth={0}>
          <Typography
            fontWeight={700}
            fontSize={14}
            textTransform="uppercase"
            noWrap
            title={ad.name}
            sx={{ letterSpacing: 0.2 }}
          >
            {ad.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {ad.adCreativeTitle}
          </Typography>
          <Box mt={0.5}>
            <Chip
              icon={
                ad.entityStatus ===
                ApiExternalEntityStatus.EXTERNAL_ENTITY_STATUS_PAUSED ? (
                  <Pause />
                ) : undefined
              }
              label={entityStatusToLabel(
                ad.entityStatus ||
                  ApiExternalEntityStatus.EXTERNAL_ENTITY_STATUS_ACTIVE,
              )}
              size="small"
              color={
                ad.entityStatus ===
                ApiExternalEntityStatus.EXTERNAL_ENTITY_STATUS_PAUSED
                  ? "warning"
                  : "success"
              }
              variant="outlined"
            />
          </Box>
        </Box>
      </Stack>

      <SectionCard colour={publisherColour}>
        <SectionHeader colour={publisherColour}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            minWidth={0}
          >
            <Campaign sx={{ fontSize: 18, color: publisherColour }} />
            <Typography
              fontSize={12}
              fontWeight={700}
              color={publisherColour}
              textTransform="uppercase"
              noWrap
            >
              {publisherName} Reported{" "}
            </Typography>
          </Stack>
        </SectionHeader>

        <SectionBody>
          <MetricsRow>
            <AdStatMetric
              label="Spend"
              value={formatMetric(
                ApiMetric.METRIC_SPEND,
                Number(data[ApiMetric.METRIC_SPEND]),
                { isKM: true },
              )}
            />
            <AdStatMetric
              label="Clicks"
              value={formatMetric(
                ApiMetric.METRIC_PUBLISHER_CLICKS,
                Number(data[ApiMetric.METRIC_PUBLISHER_CLICKS]),
                { isKM: true },
              )}
            />
            <AdStatMetric
              label="CPC"
              value={formatMetric(
                ApiMetric.METRIC_PUBLISHER_CPC,
                Number(data[ApiMetric.METRIC_PUBLISHER_CPC]),
              )}
            />
            <AdStatMetric
              label="ROAS"
              value={formatMetric(
                ApiMetric.METRIC_PUBLISHER_ROAS,
                Number(data[ApiMetric.METRIC_PUBLISHER_ROAS]),
              )}
              valueColor={publisherColour}
            />
            <ClickableTooltip
              text={"Metrics reported by the publisher."}
              placement="top"
              width={240}
              icon={<StyledHelp sx={{ fontSize: 14 }} />}
            />
          </MetricsRow>
          <AdStatSparkline
            data={publisherTrendPoints}
            colour={publisherColour}
          />
        </SectionBody>
      </SectionCard>

      <SectionCard colour={trackedColour}>
        <SectionHeader colour={trackedColour}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            minWidth={0}
          >
            <VerifiedUser sx={{ fontSize: 18, color: trackedColour }} />
            <Typography
              fontSize={12}
              fontWeight={700}
              color={trackedColour}
              textTransform="uppercase"
              noWrap
            >
              Tracked by Us ( {publisherName})
            </Typography>
          </Stack>
        </SectionHeader>

        <SectionBody>
          <MetricsRow>
            <AdStatMetric
              label="Sessions"
              value={formatMetric(
                ApiMetric.METRIC_SESSIONS_COUNT,
                Number(data[ApiMetric.METRIC_SESSIONS_COUNT]),
                { isKM: true },
              )}
            />
            <AdStatMetric
              label="Cart Rate"
              value={formatMetric(
                ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                Number(data[ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE]),
              )}
            />
            <AdStatMetric
              label="CR"
              value={formatMetric(
                ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                Number(data[ApiMetric.METRIC_MACRO_CONVERSION_RATE]),
              )}
            />
            <AdStatMetric
              label={metricToName(resolvedRoasMetric)}
              value={formatMetric(
                ApiMetric.METRIC_ROAS,
                Number(data[resolvedRoasMetric] || 0),
              )}
              valueColor={trackedColour}
            />
            <ClickableTooltip
              text={"Metrics verified by Incendium."}
              placement="top"
              width={240}
              icon={<StyledHelp sx={{ fontSize: 14 }} />}
            />
          </MetricsRow>
          <AdStatSparkline data={trackedTrendPoints} colour={trackedColour} />
        </SectionBody>
      </SectionCard>

      <VarianceRow>
        <Box>
          <Typography
            fontSize={11}
            fontWeight={700}
            color="text.secondary"
            textTransform="uppercase"
            letterSpacing={0.3}
          >
            Variance
          </Typography>
          <Typography
            fontSize={11}
            color="text.secondary"
            fontStyle="italic"
            lineHeight={1.2}
          >
            (Publisher vs Our Tracking)
          </Typography>
        </Box>
        <Box>
          <MetricLabel>Clicks</MetricLabel>
          <MetricValue
            colour={varianceColour(variance.clicks, errorColour, successColour)}
          >
            {formatVariance(variance.clicks)}
          </MetricValue>
        </Box>
        <Box>
          <MetricLabel>Sales</MetricLabel>
          <MetricValue
            colour={varianceColour(variance.sales, errorColour, successColour)}
          >
            {formatVariance(variance.sales)}
          </MetricValue>
        </Box>
        <Box>
          <MetricLabel>ROAS</MetricLabel>
          <MetricValue
            colour={varianceColour(variance.roas, errorColour, successColour)}
          >
            {formatVariance(variance.roas)}
          </MetricValue>
        </Box>
        <ClickableTooltip
          text={
            "Difference between publisher-reported and Incendium-tracked metrics."
          }
          placement="top"
          width={240}
          icon={<StyledHelp sx={{ fontSize: 14 }} />}
        />
      </VarianceRow>
    </CardRoot>
  );
}

export default AdStatCard;
